import {
  DiffResult,
  DiffRow,
  HostedSnapshotListResponse,
  HostedSnapshotRow,
  MonthlyDiffResponse,
  SnapshotSummary,
} from '../../../models/billing.model';
import { BillingService } from '../../../services/billing.service';
import { NotificationsService } from '../../../services/notifications.service';
import { DomainsService } from '../../../services/domains.service';

import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TabMenuModule } from 'primeng/tabmenu';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { MenuItem } from 'primeng/api';

import { firstValueFrom } from 'rxjs';
import { SelectButtonModule } from 'primeng/selectbutton';
import { NetsapiensDomain } from '@voice/utils';
import { HostedSnapshotsTourService } from './hosted-snapshots-tour.service';

const TOTAL_KEYS = new Set<string>([
  'Billable TNs',
  'Conference Bridge',
  'MLHG',
  'Total TNs',
  'Total Users',
]);

@Component({
  selector: 'app-hosted-snapshots',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TabMenuModule,
    TableModule,
    DropdownModule,
    ButtonModule,
    TagModule,
    BadgeModule,
    ProgressSpinnerModule,
    TooltipModule,
    CardModule,
    SelectButtonModule,
  ],
  templateUrl: './hosted-snapshots.component.html',
  styleUrls: ['./hosted-snapshots.component.scss'],
})
export class HostedSnapshotsComponent implements OnInit {
  protected readonly billingService = inject(BillingService);
  protected readonly notificationsService = inject(NotificationsService);
  protected readonly domainsService = inject(DomainsService);
  private readonly tourService = inject(HostedSnapshotsTourService);

  tabs: MenuItem[] = [
    { id: 'list', label: 'Saved Snapshots', icon: 'pi pi-database' },
    { id: 'compare', label: 'Compare Snapshots', icon: 'pi pi-exchange' },
  ];
  activeTab: MenuItem = this.tabs[0];

  compareModeOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Monthly', value: 'monthly' },
  ];
  compareMode: 'daily' | 'monthly' = 'daily';

  private allSnapshots: SnapshotSummary[] = [];
  snapshots: SnapshotSummary[] = [];

  domains: NetsapiensDomain[] = [];
  domain = '';
  limit = 100; //latest 3 months

  loadingList = false;
  selectedSnapshot?: SnapshotSummary | null = null;

  selectedSnapId = '';
  rows: HostedSnapshotRow[] = [];
  loadingRows = false;

  private rowsCache = new Map<string, HostedSnapshotRow[]>();

  fromId = '';
  toId = '';
  diff: DiffResult | null = null;
  loadingDiff = false;

  year = new Date().getFullYear();
  month = new Date().getMonth() + 1;
  monthly: MonthlyDiffResponse | null = null;
  loadingMonthly = false;

  get snapshotsForMode(): SnapshotSummary[] {
    if (this.compareMode === 'monthly') {
      return this.allSnapshots.filter((s) => s.isFinalOfMonth);
    }
    return this.allSnapshots;
  }

  onCompareModeChange() {
    const list = this.snapshotsForMode;

    const hasFrom = !!list.find((s) => s.id === this.fromId);
    const hasTo = !!list.find((s) => s.id === this.toId);

    if (!hasFrom || !hasTo) {
      const a = list[0]?.id ?? '';
      const b = list[1]?.id ?? '';
      this.toId = a || '';
      this.fromId = b || '';
    }
  }
  startTour(): void {
    this.tourService.start();
  }

  ngOnInit(): void {
    this.loadDomains();
  }

  get summaryRows(): HostedSnapshotRow[] {
    return (this.rows ?? []).filter((r) => (r.SCOPE ?? '') === '');
  }

  get detailRows(): HostedSnapshotRow[] {
    return (this.rows ?? []).filter((r) => (r.SCOPE ?? '') !== '');
  }

  async loadDomains() {
    try {
      const list = await firstValueFrom(this.domainsService.getDomains());
      this.domains = list ?? [];

      if (this.domains.length) {
        this.domain = this.domains[0].domain;
        await this.loadSnapshots();
      }
    } catch (err: unknown) {
      this.notificationsService.error(err);
    }
  }

  async loadSnapshots() {
    this.loadingList = true;
    this.diff = null;
    this.monthly = null;
    this.fromId = '';
    this.toId = '';
    this.selectedSnapshot = null;
    this.rows = [];
    this.selectedSnapId = '';
    this.rowsCache.clear();

    try {
      const res: HostedSnapshotListResponse = await firstValueFrom(
        this.billingService.getHostedSnapshots(this.domain, this.limit)
      );

      this.allSnapshots = res.items ?? [];
      this.snapshots = this.allSnapshots;

      const list = this.snapshotsForMode;
      if (list.length) {
        this.toId = list[0]?.id ?? '';
        this.fromId = list[1]?.id ?? '';

        this.selectedSnapshot = this.allSnapshots[0];
        this.selectedSnapId = this.selectedSnapshot?.id ?? '';
        if (this.selectedSnapId) await this.loadRows(this.selectedSnapId);
      }
    } catch (err: unknown) {
      this.notificationsService.error(err);
    } finally {
      this.loadingList = false;
    }
  }

  async onRowSelect() {
    if (!this.selectedSnapshot) return;
    this.selectedSnapId = this.selectedSnapshot.id;
    this.loadRows(this.selectedSnapId, false);
  }

  async loadRows(snapshotId: string, refresh = false) {
    if (!refresh && this.rowsCache.has(snapshotId)) {
      this.rows = this.rowsCache.get(snapshotId) || [];
      return;
    }

    this.loadingRows = true;
    this.rows = [];
    try {
      const res = await firstValueFrom(
        this.billingService.getHostedSnapshotRows(snapshotId)
      );
      this.rows = res.rows ?? [];
      this.rowsCache.set(snapshotId, this.rows);
    } catch (err: unknown) {
      this.notificationsService.error(err);
    } finally {
      this.loadingRows = false;
    }
  }

  forceRefreshSelected() {
    if (this.selectedSnapId) {
      this.loadRows(this.selectedSnapId, true);
    }
  }

  async compare() {
    if (!this.fromId || !this.toId) return;
    this.loadingDiff = true;
    this.diff = null;
    try {
      this.diff = await firstValueFrom(
        this.billingService.getHostedSnapshotsDiff(this.fromId, this.toId)
      );
    } catch (err: unknown) {
      this.notificationsService.error(err);
    } finally {
      this.loadingDiff = false;
    }
  }

  async compareMonthly() {
    if (!this.domain || !this.year || !this.month) return;
    this.loadingMonthly = true;
    this.monthly = null;
    try {
      this.monthly = await firstValueFrom(
        this.billingService.getHostedMonthlyDiff(
          this.domain,
          this.year,
          this.month
        )
      );
    } catch (err: unknown) {
      this.notificationsService.error(err);
    } finally {
      this.loadingMonthly = false;
    }
  }

  captureLabel(id?: string | null) {
    const snap = id ? this.snapshots.find((s) => s.id === id) : null;
    return snap ? this.toLocal(snap.capturedAt) : id ?? '';
  }

  isTotalService = (st: string) => TOTAL_KEYS.has(st);

  filterTotals(arr: DiffRow[] | undefined): DiffRow[] {
    return (arr ?? []).filter((r) => this.isTotalService(r.serviceType));
  }
  filterLines(arr: DiffRow[] | undefined): DiffRow[] {
    return (arr ?? []).filter((r) => !this.isTotalService(r.serviceType));
  }

  incTotals(arr?: DiffRow[]): DiffRow[] {
    return this.filterTotals(arr).filter((r) => r.delta > 0);
  }
  decTotals(arr?: DiffRow[]): DiffRow[] {
    return this.filterTotals(arr).filter((r) => r.delta < 0);
  }

  toLocal(iso?: string) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }

  private isTotalRow(r: DiffRow) {
    return (
      !!r &&
      (!r.site || r.site === '') &&
      (!r.scope || r.scope === '') &&
      TOTAL_KEYS.has(r.serviceType)
    );
  }

  get totalsMerged(): { name: string; delta: number }[] {
    const res: Record<string, number> = {};
    const add = (arr?: DiffRow[]) =>
      arr?.forEach((r) => {
        if (!this.isTotalRow(r)) return;
        res[r.serviceType] = (res[r.serviceType] ?? 0) + r.delta;
      });

    add(this.diff?.added);
    add(this.diff?.changed);
    add(this.diff?.removed);

    return Object.entries(res)
      .map(([name, delta]) => ({ name, delta }))
      .filter((x) => x.delta !== 0);
  }

  nonTotalRows(arr: DiffRow[] | null | undefined): DiffRow[] {
    return (arr ?? []).filter((r) => !TOTAL_KEYS.has(r.serviceType));
  }
}
